/* Hallmark · component: Drawer · genre: modern-minimal · theme: maeiz-tokens
 * motion: opacity + transform only; honors reduced motion
 */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { X } from "lucide-react";
import { usePresence } from "../../hooks/usePresence";
import { thinScrollbarClass } from "../../styles/overlay";

export interface DrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    side?: "right" | "left";
    className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
    open,
    onClose,
    title,
    children,
    footer,
    side = "right",
    className,
}) => {
    const { mounted, visible } = usePresence(open, 220);

    useEffect(() => {
        if (!mounted) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [mounted, open, onClose]);

    if (!mounted) return null;

    const panel = (
        <div className="fixed inset-0 z-[90] flex" role="dialog" aria-modal="true" aria-label={title}>
            <button
                type="button"
                className={clsx(
                    "absolute inset-0 bg-black/40 transition-opacity duration-200 ease-out motion-reduce:transition-none",
                    visible ? "opacity-100" : "opacity-0",
                )}
                aria-label="Close drawer"
                onClick={onClose}
            />
            <div
                className={clsx(
                    "relative flex h-full w-full max-w-md flex-col bg-white shadow-mz-xl dark:bg-neutral-navy",
                    "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                    side === "right" ? "ms-auto" : "me-auto",
                    visible
                        ? "translate-x-0 opacity-100"
                        : side === "right"
                          ? "translate-x-4 opacity-0"
                          : "-translate-x-4 opacity-0",
                    className,
                )}
            >
                <div className="flex shrink-0 items-center justify-between border-b border-neutral-border px-4 py-3 dark:border-white/10">
                    {title ? (
                        <h2 className="font-display text-lg font-semibold text-neutral-dark dark:text-white">{title}</h2>
                    ) : (
                        <span />
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl p-2 text-neutral-secondary hover:bg-neutral-light hover:text-neutral-ink dark:hover:bg-white/10 dark:hover:text-white"
                        aria-label="Close"
                    >
                        <X size={18} strokeWidth={2} />
                    </button>
                </div>
                <div className={clsx("min-h-0 flex-1 p-4", thinScrollbarClass)}>{children}</div>
                {footer ? (
                    <div className="shrink-0 border-t border-neutral-border px-4 py-3 dark:border-white/10">{footer}</div>
                ) : null}
            </div>
        </div>
    );

    return createPortal(panel, document.body);
};
